/* ==========================================================
   ProfessionalLearner — Three adult professional courses
   Routes: signup-a → signup-b → signup-c → home → course
   State: localStorage key 'assistivos_prof_v1'
   ========================================================== */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowRight, Award, BookOpen, Briefcase, Check, ChevronLeft,
  ChevronRight, Clock, GraduationCap, Heart, Home, Lock,
  MapPin, Shield, Star, User, Zap, AlertTriangle, Info,
  CheckSquare, Square, RefreshCw, Download, X,
} from '../../components/CustomIcons';
import { Btn, Mono, ScreenHeader } from '../Prototype';
import {
  PROF_COURSES, PROF_COURSES_LIST, CAREER_RECOMMENDATIONS,
  FACILITIES, PROF_EMPLOYERS,
} from './professionalData';

/* ── persistence ─────────────────────────────────────────── */

const STORAGE_KEY = 'assistivos_prof_v1';
const todayKey = () => new Date().toISOString().slice(0, 10);

const DEFAULT_STATE = {
  screen: 'signup-a',
  learner: { firstName: '', lastInitial: '', facility: '', education: '', career: '', enrolled: '', release: '' },
  activeCourse: null,
  activeNode: 0,
  nodePhase: 'lesson',    // 'lesson' | 'quiz'
  xp: 0,
  hearts: 3,
  heartsDay: todayKey(),
  courses: {},            // { [id]: { nodesComplete, finalPassed, certId, certDate, failedAt } }
  activeTab: 'home',      // bottom nav
  moodDone: false,
};

function useProfState() {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
    } catch (_) { /* ignore */ }
    return DEFAULT_STATE;
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) { /* ignore */ }
  }, [state]);

  // reset hearts daily
  useEffect(() => {
    const t = todayKey();
    if (state.heartsDay !== t) {
      setState(s => ({ ...s, hearts: 3, heartsDay: t }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback(
    (patch) => setState(s => ({ ...s, ...(typeof patch === 'function' ? patch(s) : patch) })),
    []
  );

  return [state, update];
}

/* ── design tokens ───────────────────────────────────────── */
const N = '#0f2744'; // deep navy
const O = '#f97316'; // signal orange
const W = '#fafaf9'; // warm white
const SL = '#64748b'; // slate
const SURF = '#f1f5f9'; // surface
const SUC = '#15803d'; // success green
const ERR = '#dc2626'; // error red

/* ── small helpers ───────────────────────────────────────── */

const Tag = ({ children, color = O }) => (
  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider"
    style={{ background: color === O ? `${O}20` : `${SUC}15`, color }}>{children}</span>
);

const Callout = ({ type, text }) => {
  const isWarning = type === 'warning';
  const isSafety = type === 'safety';
  return (
    <div className="rounded-[10px] px-4 py-4"
      style={{
        borderLeft: `4px solid ${isWarning || isSafety ? ERR : O}`,
        background: isWarning || isSafety ? 'rgba(220,38,38,0.06)' : 'rgba(249,115,22,0.07)',
      }}>
      {(isWarning || isSafety) && (
        <div className="flex items-center gap-1.5 mb-1.5">
          <AlertTriangle className="w-3.5 h-3.5" style={{ color: ERR }} />
          <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ERR }}>
            {isSafety ? 'Safety Rule' : 'Warning'}
          </span>
        </div>
      )}
      <p className="text-[14px] leading-relaxed" style={{ color: isWarning || isSafety ? '#7f1d1d' : N }}>{text}</p>
    </div>
  );
};

const XpFloat = ({ amount, onDone }) => {
  useEffect(() => { const t = setTimeout(onDone, 1200); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="fixed top-24 right-6 z-50 pointer-events-none"
      style={{ animation: 'xpFloat 1.2s ease-out forwards', fontFamily: 'var(--p-font-mono)' }}>
      <span className="rounded-full px-3 py-1 text-[14px] font-bold"
        style={{ background: O, color: N }}>+{amount} XP</span>
    </div>
  );
};

/* ── Hearts ──────────────────────────────────────────────── */
const Hearts = ({ count }) => (
  <div className="flex items-center gap-0.5" aria-label={`${count} hearts remaining`}>
    {[0, 1, 2].map(i => (
      <Heart key={i} className="w-4 h-4" fill={i < count ? ERR : 'none'}
        style={{ color: i < count ? ERR : '#cbd5e1' }} />
    ))}
  </div>
);

/* ── PathwayRail ─────────────────────────────────────────── */
const PathwayRail = ({ course, nodesComplete = [], activeNode }) => {
  const nodes = course.nodes;
  return (
    <div className="flex items-center gap-0 px-4 py-3 overflow-x-auto"
      style={{ background: SURF, borderBottom: '1px solid #e2e8f0' }}>
      {nodes.map((n, i) => {
        const done = nodesComplete.includes(n.id);
        const current = i === activeNode;
        const locked = !done && !current && (i > 0) && !nodesComplete.includes(nodes[i - 1]?.id) && i !== activeNode;
        return (
          <React.Fragment key={n.id}>
            {i > 0 && (
              <div className="w-8 h-0.5 shrink-0"
                style={{ background: done || (i <= (nodesComplete.length)) ? O : '#e2e8f0' }} />
            )}
            <div className="shrink-0 flex flex-col items-center gap-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[12px] font-bold transition-all`}
                style={{
                  background: done ? O : current ? N : '#e2e8f0',
                  color: done ? N : current ? W : SL,
                  border: current ? `2px solid ${O}` : 'none',
                }}>
                {done ? <Check className="w-4 h-4" /> : locked ? <Lock className="w-3 h-3" /> : i + 1}
              </div>
              <span className="text-[9px] font-semibold uppercase tracking-wide text-center w-14 leading-tight"
                style={{ color: current ? N : SL }}>
                {n.id === 'final' || n.kind === 'final' ? 'Final' : `Node ${i + 1}`}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ── BottomNav ───────────────────────────────────────────── */
const ProfBottomNav = ({ active, onChange, courses }) => {
  const earnedCount = Object.values(courses || {}).filter(c => c.finalPassed).length;
  const tabs = [
    { key: 'home', label: 'Home', Icon: Home },
    { key: 'creds', label: 'Credentials', Icon: Award, badge: earnedCount },
    { key: 'jobs', label: 'Jobs', Icon: Briefcase },
    { key: 'reentry', label: 'Reentry', Icon: MapPin },
  ];
  return (
    <nav className="flex border-t" style={{ borderColor: '#e2e8f0', background: W }}
      aria-label="Main navigation">
      {tabs.map(({ key, label, Icon, badge }) => (
        <button key={key} onClick={() => onChange(key)}
          className="flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold uppercase tracking-wide relative"
          style={{ color: active === key ? O : SL }}
          data-testid={`prof-nav-${key}`}
          aria-current={active === key ? 'page' : undefined}>
          <div className="relative">
            <Icon className="w-5 h-5" />
            {badge > 0 && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full text-[8px] font-bold flex items-center justify-center"
                style={{ background: O, color: N }}>{badge}</span>
            )}
          </div>
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
};

/* ── Node renderers ──────────────────────────────────────── */

/** STORY node */
const StoryNode = ({ node }) => (
  <div className="space-y-4 p-5">
    <div className="space-y-3">
      {node.body.map((para, i) => (
        <p key={i} className="text-[15px] leading-[1.65]" style={{ color: '#1c1c1e' }}>{para}</p>
      ))}
    </div>
    {node.callout && <Callout type={node.callout.type || 'key'} text={node.callout.text} />}
    {node.cards && (
      <div className="space-y-2 pt-2">
        <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: SL }}>Core Definitions</p>
        {node.cards.map((card, i) => (
          <div key={i} className="rounded-[10px] p-4" style={{ background: SURF, border: '1px solid #e2e8f0' }}>
            <p className="text-[12px] font-bold uppercase tracking-wider mb-1.5" style={{ color: N }}>{card.title}</p>
            <p className="text-[14px] leading-relaxed" style={{ color: '#1c1c1e' }}>{card.body}</p>
          </div>
        ))}
      </div>
    )}
  </div>
);

/** CARDS node — tap to flip */
const CardsNode = ({ node }) => {
  const [flipped, setFlipped] = useState({});
  const allFlipped = node.cards.every((_, i) => flipped[i]);

  return (
    <div className="p-5 space-y-4">
      {node.body && node.body.map((para, i) => (
        <p key={i} className="text-[15px] leading-relaxed" style={{ color: '#1c1c1e' }}>{para}</p>
      ))}
      {node.intro && <p className="text-[15px] leading-relaxed" style={{ color: '#1c1c1e' }}>{node.intro}</p>}
      <p className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: SL }}>
        Tap each card to reveal the details
      </p>
      <div className="space-y-3">
        {node.cards.map((card, i) => (
          <button key={i} onClick={() => setFlipped(f => ({ ...f, [i]: !f[i] }))}
            className="w-full text-left rounded-[12px] overflow-hidden transition-all"
            style={{ background: flipped[i] ? N : SURF, border: `1px solid ${flipped[i] ? N : '#e2e8f0'}` }}
            data-testid={`prof-card-${i}`}>
            <div className="flex items-center justify-between px-4 py-3.5">
              <div className="flex-1">
                <p className="text-[11px] font-bold uppercase tracking-wider mb-0.5"
                  style={{ color: flipped[i] ? `${O}` : SL }}>{card.title}</p>
                <p className="text-[14px] font-semibold"
                  style={{ color: flipped[i] ? W : N }}>{card.front}</p>
              </div>
              <ChevronRight className="w-4 h-4 shrink-0 ml-2 transition-transform"
                style={{ color: flipped[i] ? O : SL, transform: flipped[i] ? 'rotate(90deg)' : 'none' }} />
            </div>
            {flipped[i] && (
              <div className="px-4 pb-4 pt-1 border-t" style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
                <p className="text-[14px] leading-relaxed whitespace-pre-line" style={{ color: 'rgba(250,250,249,0.9)' }}>
                  {card.back}
                </p>
                {card.spec && (
                  <div className="mt-3 pt-3 border-t grid grid-cols-1 gap-1.5" style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
                    <div className="flex gap-2"><span className="text-[11px] font-bold uppercase" style={{ color: O }}>Job:</span><span className="text-[12px]" style={{ color: W }}>{card.spec.job}</span></div>
                    <div className="flex gap-2"><span className="text-[11px] font-bold uppercase" style={{ color: O }}>Primary Fault:</span><span className="text-[12px]" style={{ color: W }}>{card.spec.fault}</span></div>
                  </div>
                )}
              </div>
            )}
          </button>
        ))}
      </div>
      {!allFlipped && (
        <p className="text-[12px] text-center" style={{ color: SL }}>Flip all {node.cards.length} cards to continue</p>
      )}
      {node.netMetering && (
        <div className="rounded-[10px] p-4" style={{ background: 'rgba(249,115,22,0.07)', border: `1px solid ${O}33` }}>
          <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: O }}>{node.netMetering.title}</p>
          <p className="text-[13px] leading-relaxed whitespace-pre-line" style={{ color: N }}>{node.netMetering.text}</p>
        </div>
      )}
    </div>
  );
};

/** STEPS node */
const StepsNode = ({ node }) => {
  return (
    <div className="p-5 space-y-4">
      {node.intro && <p className="text-[15px] leading-relaxed" style={{ color: '#1c1c1e' }}>{node.intro}</p>}
      <ol className="space-y-3 list-none p-0">
        {node.steps.map((step, i) => (
          <li key={i} className="flex gap-4">
            <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-[13px]"
              style={{ background: N, color: W }}>{step.num}</div>
            <div className="flex-1">
              <p className="text-[13px] font-bold uppercase tracking-wider mb-1" style={{ color: N }}>{step.title}</p>
              <p className="text-[14px] leading-relaxed" style={{ color: '#1c1c1e' }}>{step.text}</p>
            </div>
          </li>
        ))}
      </ol>
      {node.warning && <Callout type="warning" text={node.warning.text} />}
    </div>
  );
};

/** LOOP / SPA node */
const LoopNode = ({ node }) => {
  const [revealedIdx, setRevealedIdx] = useState(-1);

  return (
    <div className="p-5 space-y-5">
      {node.intro && <p className="text-[15px] leading-relaxed" style={{ color: '#1c1c1e' }}>{node.intro}</p>}
      {node.setup && (
        <div className="space-y-1.5">
          {node.setup.map((s, i) => (
            <p key={i} className="text-[14px] leading-relaxed" style={{ color: '#1c1c1e' }}>{s}</p>
          ))}
        </div>
      )}

      {/* Loop diagram */}
      <div className="flex items-center justify-center gap-2 py-3">
        {node.loop.map((step, i) => (
          <React.Fragment key={step.label}>
            {i > 0 && <ArrowRight className="w-4 h-4 shrink-0" style={{ color: SL }} />}
            <div className="flex flex-col items-center gap-1">
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-[11px] font-bold text-center leading-tight"
                style={{ background: step.color, color: W, border: `3px solid ${step.color}` }}>
                {step.label}
              </div>
              <span className="text-[10px] text-center w-16 leading-tight" style={{ color: SL }}>
                {step.text}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Scale display (for solar cell) */}
      {node.scale && (
        <div className="rounded-[10px] p-4" style={{ background: SURF }}>
          <p className="text-[12px] font-bold uppercase tracking-wider mb-1.5" style={{ color: N }}>{node.scale.title}</p>
          <p className="text-[14px] leading-relaxed" style={{ color: '#1c1c1e' }}>{node.scale.text}</p>
        </div>
      )}

      {/* Tap-to-reveal scenarios */}
      {node.scenarios && (
        <div className="space-y-2">
          <p className="text-[12px] font-bold uppercase tracking-wider" style={{ color: SL }}>
            Tap to identify the fault
          </p>
          {node.scenarios.map((sc, i) => (
            <button key={i} onClick={() => setRevealedIdx(revealedIdx === i ? -1 : i)}
              className="w-full text-left rounded-[10px] px-4 py-3.5 transition-all"
              style={{ background: revealedIdx === i ? N : SURF, border: `1px solid ${revealedIdx === i ? N : '#e2e8f0'}` }}
              data-testid={`prof-scenario-${i}`}>
              <p className="text-[14px] font-semibold" style={{ color: revealedIdx === i ? W : N }}>
                {sc.trigger}
              </p>
              {revealedIdx === i && (
                <div className="mt-2.5 pt-2.5 border-t" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                  <span className="text-[11px] font-bold uppercase" style={{ color: O }}>→ {sc.step}</span>
                  <p className="text-[13px] mt-1 leading-relaxed" style={{ color: 'rgba(250,250,249,0.88)' }}>{sc.text}</p>
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** TIERS node (Agentic Modeling — autonomy staircase) */
const TiersNode = ({ node }) => {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="p-5 space-y-4">
      {node.intro && <p className="text-[15px] leading-relaxed" style={{ color: '#1c1c1e' }}>{node.intro}</p>}
      <div className="space-y-2">
        {node.tiers.map((tier) => (
          <div key={tier.num} className="rounded-[10px] px-4 py-4 relative"
            style={{ background: tier.highlight ? N : SURF, border: `1px solid ${tier.highlight ? N : '#e2e8f0'}`, marginLeft: `${(tier.num - 1) * 8}px` }}>
            {tier.note && (
              <div className="absolute -top-2 right-3 rounded-full px-2 py-0.5 text-[10px] font-bold"
                style={{ background: O, color: N }}>{tier.note}</div>
            )}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider"
                style={{ color: tier.highlight ? O : SL }}>Tier {tier.num}</span>
              <span className="text-[13px] font-bold"
                style={{ color: tier.highlight ? W : N }}>{tier.title}</span>
            </div>
            <p className="text-[13px] leading-relaxed"
              style={{ color: tier.highlight ? 'rgba(250,250,249,0.85)' : '#1c1c1e' }}>{tier.text}</p>
          </div>
        ))}
      </div>
      {node.scenario && (
        <div className="space-y-2">
          <p className="text-[14px] font-semibold" style={{ color: N }}>{node.scenario.q}</p>
          <button onClick={() => setRevealed(true)}
            className="w-full rounded-[10px] px-4 py-3.5 text-left text-[14px] font-semibold"
            style={{ background: revealed ? N : SURF, color: revealed ? W : SL, border: `1px solid ${revealed ? N : '#e2e8f0'}` }}
            data-testid="prof-tier-scenario">
            {revealed ? node.scenario.answer : 'Tap to reveal'}
          </button>
        </div>
      )}
    </div>
  );
};

/** REGISTER node (Agentic — consequential action table) */
const RegisterNode = ({ node }) => (
  <div className="p-5 space-y-4">
    {node.intro && <p className="text-[15px] leading-relaxed" style={{ color: '#1c1c1e' }}>{node.intro}</p>}
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: N, color: W }}>
            <th className="text-left p-2.5 font-bold">Action</th>
            <th className="text-left p-2.5 font-bold">Checkpoint</th>
            <th className="text-left p-2.5 font-bold hidden sm:table-cell">Approver</th>
          </tr>
        </thead>
        <tbody>
          {node.register.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? SURF : W, borderBottom: '1px solid #e2e8f0' }}>
              <td className="p-2.5 font-semibold" style={{ color: N }}>{row.action}</td>
              <td className="p-2.5" style={{ color: '#1c1c1e' }}>{row.checkpoint}</td>
              <td className="p-2.5 hidden sm:table-cell" style={{ color: SL }}>{row.approver}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {node.principle && (
      <div className="rounded-[10px] px-4 py-4" style={{ borderLeft: `4px solid ${O}`, background: `${O}0f` }}>
        <p className="text-[12px] font-bold uppercase tracking-wider mb-1.5" style={{ color: N }}>{node.principle.title}</p>
        <p className="text-[14px] leading-relaxed" style={{ color: '#1c1c1e' }}>{node.principle.text}</p>
      </div>
    )}
    {node.promoGate && (
      <div className="rounded-[10px] px-4 py-4" style={{ background: SURF, border: '1px solid #e2e8f0' }}>
        <p className="text-[12px] font-bold uppercase tracking-wider mb-1.5" style={{ color: N }}>{node.promoGate.title}</p>
        <p className="text-[14px] leading-relaxed" style={{ color: '#1c1c1e' }}>{node.promoGate.text}</p>
      </div>
    )}
  </div>
);

/** CHECKLIST node */
const ChecklistNode = ({ node }) => {
  const [checked, setChecked] = useState([]);
  const allDone = checked.length === node.items.length;
  const toggle = (i) => setChecked(c => c.includes(i) ? c.filter(x => x !== i) : [...c, i]);

  return (
    <div className="p-5 space-y-4">
      {node.intro && <p className="text-[15px] leading-relaxed" style={{ color: '#1c1c1e' }}>{node.intro}</p>}
      <div className="space-y-2">
        {node.items.map((item, i) => (
          <button key={i} onClick={() => toggle(i)}
            className="w-full flex items-start gap-3 text-left rounded-[10px] px-4 py-3.5 transition-all"
            style={{ background: checked.includes(i) ? `${SUC}12` : SURF, border: `1px solid ${checked.includes(i) ? SUC : '#e2e8f0'}` }}
            data-testid={`prof-checklist-${i}`}>
            {checked.includes(i)
              ? <CheckSquare className="w-5 h-5 shrink-0 mt-0.5" style={{ color: SUC }} />
              : <Square className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#cbd5e1' }} />}
            <span className="text-[14px] leading-relaxed" style={{ color: checked.includes(i) ? SUC : '#1c1c1e' }}>
              {item}
            </span>
          </button>
        ))}
      </div>
      {allDone && (
        <div className="rounded-[10px] px-4 py-3 flex items-center gap-2"
          style={{ background: `${SUC}12`, border: `1px solid ${SUC}` }}>
          <Check className="w-4 h-4" style={{ color: SUC }} />
          <p className="text-[14px] font-semibold" style={{ color: SUC }}>All pre-deployment steps confirmed.</p>
        </div>
      )}
    </div>
  );
};

/** SAFETY node */
const SafetyNode = ({ node, onScenarioDone, scenarioDone }) => {
  const [chosen, setChosen] = useState(null);

  const pick = (i) => {
    if (chosen !== null) return;
    setChosen(i);
    const correct = node.scenario.options[i].c;
    if (correct && !scenarioDone) onScenarioDone();
  };

  return (
    <div className="space-y-4">
      {/* Safety banner */}
      <div className="mx-5 mt-5 rounded-[10px] px-4 py-4 flex items-start gap-3"
        style={{ background: ERR, color: W }}>
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <p className="text-[13px] font-bold leading-snug">{node.banner}</p>
      </div>

      <div className="px-5 space-y-3">
        {node.body.map((para, i) => (
          <p key={i} className="text-[15px] leading-relaxed" style={{ color: '#1c1c1e' }}>{para}</p>
        ))}
        {node.rapidShutdown && (
          <div className="rounded-[10px] p-4" style={{ background: SURF, border: '1px solid #e2e8f0' }}>
            <p className="text-[12px] font-bold uppercase tracking-wider mb-1.5" style={{ color: N }}>{node.rapidShutdown.title}</p>
            <p className="text-[14px] leading-relaxed whitespace-pre-line" style={{ color: '#1c1c1e' }}>{node.rapidShutdown.text}</p>
          </div>
        )}
        {node.rule && (
          <Callout type="safety" text={node.rule.text} />
        )}

        {/* Scenario */}
        {node.scenario && (
          <div className="space-y-3">
            <p className="text-[15px] font-semibold" style={{ color: N }}>{node.scenario.q}</p>
            {node.scenario.options.map((opt, i) => {
              const picked = chosen === i;
              const correct = opt.c;
              let bg = SURF, border = '#e2e8f0', textColor = '#1c1c1e';
              if (chosen !== null) {
                if (correct) { bg = `${SUC}12`; border = SUC; textColor = SUC; }
                else if (picked) { bg = `${ERR}0d`; border = ERR; textColor = ERR; }
              }
              return (
                <button key={i} onClick={() => pick(i)}
                  disabled={chosen !== null}
                  className="w-full text-left rounded-[10px] px-4 py-3.5 text-[14px] font-semibold transition-all"
                  style={{ background: bg, border: `1px solid ${border}`, color: textColor }}
                  data-testid={`prof-scenario-opt-${i}`}>
                  {opt.t}
                </button>
              );
            })}
            {chosen !== null && (
              <div className="rounded-[10px] px-4 py-3" style={{ background: `${SUC}12`, border: `1px solid ${SUC}` }}>
                <p className="text-[13px] leading-relaxed" style={{ color: '#166534' }}>{node.scenario.feedback}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── QuizRunner (for nodes 1-4) ──────────────────────────── */
const QuizRunner = ({ quiz, hearts, onCorrect, onWrong, onComplete, courseId }) => {
  const [qIdx, setQIdx] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [shake, setShake] = useState(false);

  const q = quiz[qIdx];
  const correctIdx = q.options.findIndex(o => o.c);

  const pick = (i) => {
    if (chosen !== null) return;
    const correct = q.options[i].c;
    setChosen(i);
    if (correct) {
      onCorrect();
    } else {
      setShake(true);
      onWrong();
      setTimeout(() => setShake(false), 600);
    }
  };

  const next = () => {
    if (qIdx < quiz.length - 1) {
      setQIdx(q => q + 1);
      setChosen(null);
    } else {
      onComplete();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-2">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: SL }}>
            Question <Mono>{qIdx + 1}</Mono> of <Mono>{quiz.length}</Mono>
          </span>
          <Hearts count={hearts} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 space-y-3 pb-4">
        <p className="text-[16px] font-semibold leading-snug" style={{ color: N }}>{q.q}</p>

        <div className="space-y-2.5" role="radiogroup">
          {q.options.map((opt, i) => {
            const picked = chosen === i;
            const correct = i === correctIdx;
            let bg = SURF, border = '#e2e8f0', textColor = '#1c1c1e';
            if (chosen !== null) {
              if (correct) { bg = `${SUC}12`; border = SUC; textColor = '#166534'; }
              else if (picked) { bg = `${ERR}0d`; border = ERR; textColor = ERR; }
            }
            return (
              <button key={i} onClick={() => pick(i)} disabled={chosen !== null}
                className={`w-full text-left rounded-[12px] px-4 py-3.5 border text-[14px] flex items-center justify-between gap-3 min-h-[48px] transition-all ${shake && picked ? 'prof-shake' : ''}`}
                style={{ background: bg, border: `1px solid ${border}`, color: textColor }}
                role="radio" aria-checked={picked}
                data-testid={`prof-quiz-opt-${i}`}>
                <span>{opt.t}</span>
                {chosen !== null && correct && <Check className="w-4 h-4 shrink-0" style={{ color: SUC }} />}
                {chosen !== null && picked && !correct && <X className="w-4 h-4 shrink-0" style={{ color: ERR }} />}
              </button>
            );
          })}
        </div>

        {chosen !== null && (
          <div className="rounded-[12px] p-3.5 mt-2"
            style={{
              background: q.options[chosen].c ? `${SUC}0f` : `${O}0f`,
              border: `1px solid ${q.options[chosen].c ? SUC : O}`,
            }}>
            {q.options[chosen].c ? (
              <p className="text-[13px] font-semibold" style={{ color: '#166534' }}>Correct. <span className="font-normal">{q.explanation || ''}</span></p>
            ) : (
              <p className="text-[13px]" style={{ color: '#92400e' }}>
                Incorrect. Correct answer: <strong>{q.options[correctIdx].t}</strong>
              </p>
            )}
          </div>
        )}
      </div>

      {chosen !== null && (
        <div className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0' }}>
          <Btn onClick={next} className="w-full" data-testid="prof-quiz-next">
            {qIdx < quiz.length - 1 ? 'Next Question' : 'Complete Node'} <ArrowRight className="w-4 h-4" />
          </Btn>
        </div>
      )}
    </div>
  );
};

/* ── FinalQuiz ───────────────────────────────────────────── */
const FinalQuiz = ({ quiz, passCount, onPass, onFail }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [score, setScore] = useState(null);

  const allAnswered = Object.keys(answers).length === quiz.length;

  const submit = () => {
    const correct = quiz.filter((q, i) => q.options[answers[i]]?.c).length;
    setScore(correct);
    setSubmitted(true);
    setConfirmOpen(false);
    if (correct >= passCount) {
      setTimeout(() => onPass(correct, quiz.length), 800);
    } else {
      setTimeout(() => onFail(correct, quiz.length), 800);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <span className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: SL }}>
          <Mono>{Object.keys(answers).length}</Mono> / <Mono>{quiz.length}</Mono> answered
        </span>
        <Tag color={O}>Final Quiz</Tag>
      </div>

      <div className="flex-1 overflow-y-auto px-5 space-y-5 pb-4">
        {quiz.map((q, qi) => (
          <div key={qi} className="space-y-2.5">
            <p className="text-[15px] font-semibold leading-snug" style={{ color: N }}>
              <span style={{ color: SL }}>{qi + 1}.</span> {q.q}
            </p>
            <div className="space-y-2" role="radiogroup">
              {q.options.map((opt, oi) => {
                const picked = answers[qi] === oi;
                let bg = SURF, border = '#e2e8f0', textColor = '#1c1c1e';
                if (submitted) {
                  if (opt.c) { bg = `${SUC}12`; border = SUC; textColor = '#166534'; }
                  else if (picked && !opt.c) { bg = `${ERR}0d`; border = ERR; textColor = ERR; }
                } else if (picked) { bg = N; border = N; textColor = W; }
                return (
                  <button key={oi}
                    onClick={() => !submitted && setAnswers(a => ({ ...a, [qi]: oi }))}
                    disabled={submitted}
                    className="w-full text-left rounded-[10px] px-4 py-3 border text-[13px] transition-all"
                    style={{ background: bg, border: `1px solid ${border}`, color: textColor }}
                    role="radio" aria-checked={picked}
                    data-testid={`prof-final-opt-${qi}-${oi}`}>
                    {opt.t}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!submitted && (
        <div className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0' }}>
          <Btn onClick={() => setConfirmOpen(true)}
            disabled={!allAnswered}
            className={`w-full ${!allAnswered ? 'opacity-40 pointer-events-none' : ''}`}
            data-testid="prof-final-submit">
            Submit Answers <ArrowRight className="w-4 h-4" />
          </Btn>
        </div>
      )}

      {submitted && score !== null && (
        <div className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0', background: SURF }}>
          <p className="text-[14px] font-semibold text-center" style={{ color: N }}>
            Score: <Mono>{score}</Mono>/<Mono>{quiz.length}</Mono> — {Math.round((score / quiz.length) * 100)}%
          </p>
        </div>
      )}

      {/* Confirm modal */}
      {confirmOpen && (
        <div className="absolute inset-0 z-50 flex items-end justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full rounded-[16px] p-6 space-y-4" style={{ background: W }}>
            <h2 className="text-[18px] font-bold" style={{ color: N }}>Ready to submit?</h2>
            <p className="text-[14px]" style={{ color: SL }}>Answers cannot be changed after submission.</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmOpen(false)}
                className="flex-1 rounded-[10px] py-3 text-[15px] font-semibold"
                style={{ background: SURF, color: N }}>Cancel</button>
              <Btn onClick={submit} className="flex-1" data-testid="prof-final-confirm">Submit</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── BadgeUnlock animation ───────────────────────────────── */
const BadgeUnlock = ({ course, onDone }) => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 400);
    const t2 = setTimeout(() => setPhase(2), 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div className="absolute inset-0 z-50 flex flex-col items-center justify-center"
      style={{ background: 'rgba(15,39,68,0.96)', backdropFilter: 'blur(8px)' }}>
      <div className={`transition-all duration-700 ${phase >= 1 ? 'scale-100 opacity-100' : 'scale-50 opacity-0'}`}>
        <div className="w-28 h-28 rounded-full flex items-center justify-center text-5xl mb-6 shadow-xl"
          style={{ background: course.badgeAccent, boxShadow: `0 0 40px ${course.badgeAccent}66` }}>
          {course.badgeEmoji}
        </div>
      </div>
      <div className={`text-center transition-all duration-500 delay-300 ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <p className="text-[11px] font-bold uppercase tracking-widest mb-1" style={{ color: O }}>Badge Unlocked</p>
        <h2 className="p-display text-[22px] mb-1" style={{ color: W }}>{course.badgeName}</h2>
        <p className="text-[13px] mb-6" style={{ color: 'rgba(250,250,249,0.65)' }}>+100 XP Bonus</p>
        <Btn onClick={onDone} data-testid="prof-badge-continue">
          View Certificate <ArrowRight className="w-4 h-4" />
        </Btn>
      </div>
    </div>
  );
};

/* ── CertificateCard ─────────────────────────────────────── */
const CertificateCard = ({ course, learner, certId, certDate, onBack }) => {
  const dateStr = certDate ? new Date(certDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : new Date().toLocaleDateString();

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="flex flex-col h-full">
      <ScreenHeader title="Certificate of Completion" subtitle={course.credential} onBack={onBack} />
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {/* Certificate */}
        <div id="prof-certificate"
          className="rounded-[16px] overflow-hidden print:rounded-none"
          style={{ background: W, border: `2px solid ${N}`, boxShadow: '0 4px 24px rgba(15,39,68,0.15)' }}>
          {/* Top accent */}
          <div className="h-3" style={{ background: `linear-gradient(90deg, ${N} 0%, ${O} 100%)` }} />

          <div className="px-6 py-5 space-y-4">
            {/* Header */}
            <div className="text-center space-y-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: SL }}>
                Certificate of Completion
              </p>
              <h1 className="p-display text-[20px] leading-tight text-center" style={{ color: N }}>
                {course.credential}
              </h1>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
              <Star className="w-4 h-4" style={{ color: O, fill: O }} />
              <div className="flex-1 h-px" style={{ background: '#e2e8f0' }} />
            </div>

            {/* Recipient */}
            <div className="text-center">
              <p className="text-[12px]" style={{ color: SL }}>Awarded to</p>
              <p className="p-display text-[26px]" style={{ color: N }}>
                {learner.firstName} {learner.lastInitial}.
              </p>
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-[8px] p-3 text-center" style={{ background: SURF }}>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: SL }}>Date</p>
                <p className="text-[12px] font-semibold p-mono" style={{ color: N }}>{dateStr}</p>
              </div>
              <div className="rounded-[8px] p-3 text-center" style={{ background: SURF }}>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: SL }}>Clock Hours</p>
                <p className="text-[12px] font-semibold p-mono" style={{ color: N }}>{course.hours} hrs</p>
              </div>
            </div>

            {/* Issuer */}
            <div className="text-center space-y-0.5">
              <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: SL }}>Issued by</p>
              <p className="text-[13px] font-bold" style={{ color: N }}>HDG University</p>
              <p className="text-[12px]" style={{ color: SL }}>Tech Tradesmith Institute</p>
            </div>

            {/* QR placeholder + ID */}
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-[8px] flex items-center justify-center shrink-0"
                style={{ background: N }}>
                {/* CSS QR placeholder */}
                <div className="grid grid-cols-4 gap-px w-10 h-10">
                  {Array.from({ length: 16 }).map((_, i) => (
                    <div key={i} className="rounded-sm"
                      style={{ background: [0,1,4,5,8,10,11,13,15].includes(i) ? W : 'transparent' }} />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: SL }}>Certificate ID</p>
                <p className="p-mono text-[12px] font-semibold" style={{ color: N }}>{certId}</p>
                {course.nabcepNote && (
                  <p className="text-[10px] mt-1 leading-tight" style={{ color: SL }}>
                    Aligns with NABCEP PVA JTA
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Bottom accent */}
          <div className="h-1.5" style={{ background: O }} />
        </div>

        {/* Actions */}
        <div className="mt-4 space-y-2">
          <Btn onClick={handlePrint} className="w-full" data-testid="prof-cert-download">
            <Download className="w-4 h-4" /> Download Certificate PDF
          </Btn>
          <button onClick={() => {}}
            className="w-full rounded-[12px] px-6 py-3 text-[15px] font-semibold"
            style={{ background: SURF, color: N }}
            data-testid="prof-cert-wallet">
            <Award className="w-4 h-4 inline mr-2" />
            Add to Credential Wallet
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Retake Timer ────────────────────────────────────────── */
const RetakeTimer = ({ failedAt, onUnlocked }) => {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    const tick = () => {
      const elapsed = Date.now() - failedAt;
      const wait = 24 * 60 * 60 * 1000; // 24 hours
      const rem = Math.max(0, wait - elapsed);
      setRemaining(rem);
      if (rem <= 0) onUnlocked();
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [failedAt, onUnlocked]);

  const h = Math.floor(remaining / (1000 * 60 * 60));
  const m = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const s = Math.floor((remaining % (1000 * 60)) / 1000);
  const fmt = n => String(n).padStart(2, '0');

  return (
    <div className="rounded-[12px] px-5 py-4 text-center" style={{ background: SURF, border: '1px solid #e2e8f0' }}>
      <p className="text-[12px] font-bold uppercase tracking-wider mb-1" style={{ color: SL }}>Retake available in</p>
      <p className="p-mono text-[28px] font-bold" style={{ color: N }}>
        {fmt(h)}:{fmt(m)}:{fmt(s)}
      </p>
    </div>
  );
};

/* ── Main screens ────────────────────────────────────────── */

const SignupA = ({ state, update }) => {
  const [form, setForm] = useState({ firstName: '', lastInitial: '', facility: '' });
  const canNext = form.firstName.trim() && form.lastInitial.trim() && form.facility;

  return (
    <>
      <ScreenHeader title="Get Started" subtitle="Step 1 of 3 · Basic Information" />
      <main className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
        <div className="rounded-[12px] p-5" style={{ background: `linear-gradient(140deg, ${N} 0%, #081827 100%)`, color: W }}>
          <Tag>Personalized Learning</Tag>
          <h2 className="p-display text-[20px] leading-snug mt-2" style={{ color: W }}>
            Answer a few questions to match with your learning pathway.
          </h2>
        </div>

        <div className="space-y-1">
          <label className="text-[13px] font-semibold" style={{ color: N }}>First name</label>
          <input value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
            className="w-full rounded-[10px] px-4 py-3 text-[15px] border outline-none focus:border-orange-500"
            style={{ background: SURF, borderColor: '#e2e8f0', color: N, fontFamily: 'var(--p-font-body)' }}
            placeholder="First name" data-testid="prof-input-firstname" />
        </div>

        <div className="space-y-1">
          <label className="text-[13px] font-semibold" style={{ color: N }}>Last initial (privacy)</label>
          <input value={form.lastInitial} maxLength={1}
            onChange={e => setForm(f => ({ ...f, lastInitial: e.target.value.toUpperCase() }))}
            className="w-full rounded-[10px] px-4 py-3 text-[15px] border outline-none"
            style={{ background: SURF, borderColor: '#e2e8f0', color: N, fontFamily: 'var(--p-font-body)' }}
            placeholder="E.g. M" data-testid="prof-input-initial" />
        </div>

        <div className="space-y-2">
          <label className="text-[13px] font-semibold" style={{ color: N }}>Facility</label>
          {FACILITIES.map(f => (
            <button key={f} onClick={() => setForm(v => ({ ...v, facility: f }))}
              className="w-full text-left rounded-[10px] px-4 py-3 text-[14px] border transition-all"
              style={{ background: form.facility === f ? N : SURF, color: form.facility === f ? W : '#1c1c1e', border: `1px solid ${form.facility === f ? N : '#e2e8f0'}` }}
              data-testid={`prof-facility-${f.replace(/\s+/g, '-').toLowerCase()}`}>
              {f}
            </button>
          ))}
        </div>
      </main>
      <footer className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0' }}>
        <Btn onClick={() => update({ learner: { ...state.learner, ...form }, screen: 'signup-b' })}
          disabled={!canNext} className={`w-full ${!canNext ? 'opacity-40 pointer-events-none' : ''}`}
          data-testid="prof-signup-a-next">
          Continue <ArrowRight className="w-4 h-4" />
        </Btn>
      </footer>
    </>
  );
};

const SignupB = ({ state, update }) => {
  const [form, setForm] = useState({
    education: state.learner.education || '',
    career: state.learner.career || '',
    enrolled: state.learner.enrolled || '',
    release: state.learner.release || '',
  });
  const canNext = form.education && form.career && form.enrolled && form.release;

  const Q = ({ label, name, opts }) => (
    <fieldset className="space-y-2 border-0 p-0" style={{ minInlineSize: 0 }}>
      <legend className="text-[14px] font-semibold mb-2" style={{ color: N }}>{label}</legend>
      {opts.map(o => (
        <button key={o} onClick={() => setForm(f => ({ ...f, [name]: o }))}
          className="w-full text-left rounded-[10px] px-4 py-3 text-[14px] border transition-all"
          style={{ background: form[name] === o ? N : SURF, color: form[name] === o ? W : '#1c1c1e', border: `1px solid ${form[name] === o ? N : '#e2e8f0'}` }}
          role="radio" aria-checked={form[name] === o}
          data-testid={`prof-intake-${name}-${o.replace(/[\s&]/g, '-').toLowerCase()}`}>
          {o}
        </button>
      ))}
    </fieldset>
  );

  return (
    <>
      <ScreenHeader title="Tell Us About You" subtitle="Step 2 of 3 · Learning Profile"
        onBack={() => update({ screen: 'signup-a' })} />
      <main className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
        <Q label="Education level" name="education"
          opts={['High school diploma or GED', 'Some college', 'Trade certification', 'Other']} />
        <Q label="Career interest" name="career"
          opts={['Technology & AI', 'Solar & Clean Energy', 'Skilled Trades', 'Healthcare', 'Business & Finance']} />
        <Q label="Currently in a facility program?" name="enrolled"
          opts={['Yes', 'No']} />
        <Q label="Estimated release date" name="release"
          opts={['Less than 6 months', '6–12 months', '1–2 years', 'More than 2 years']} />
      </main>
      <footer className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0' }}>
        <Btn onClick={() => update({ learner: { ...state.learner, ...form }, screen: 'signup-c' })}
          disabled={!canNext} className={`w-full ${!canNext ? 'opacity-40 pointer-events-none' : ''}`}
          data-testid="prof-signup-b-next">
          See My Recommendations <ArrowRight className="w-4 h-4" />
        </Btn>
      </footer>
    </>
  );
};

const SignupC = ({ state, update }) => {
  const recs = CAREER_RECOMMENDATIONS[state.learner.career] || ['robotics', 'agentic', 'solar'];
  const courses = [
    ...recs.map(id => PROF_COURSES[id]),
    ...PROF_COURSES_LIST.filter(c => !recs.includes(c.id)),
  ].slice(0, 3);

  return (
    <>
      <ScreenHeader title="Recommended Pathways" subtitle="Step 3 of 3 · Your Matches"
        onBack={() => update({ screen: 'signup-b' })} />
      <main className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        <p className="text-[15px] leading-relaxed" style={{ color: '#1c1c1e' }}>
          Based on your interest in <strong>{state.learner.career}</strong>, these pathways are recommended for you.
        </p>
        {courses.map((c, i) => (
          <button key={c.id} onClick={() => update({ screen: 'home', activeCourse: null })}
            className="w-full text-left rounded-[14px] overflow-hidden transition-all hover:shadow-md"
            style={{ border: '1px solid #e2e8f0' }}
            data-testid={`prof-rec-${c.id}`}>
            <div className="px-5 py-4 flex items-start gap-4"
              style={{ background: i === 0 ? N : SURF }}>
              <div className="w-12 h-12 rounded-[10px] shrink-0 flex items-center justify-center text-2xl"
                style={{ background: i === 0 ? `${O}33` : `${N}12` }}>
                {c.badgeEmoji}
              </div>
              <div className="flex-1 min-w-0">
                {i === 0 && <Tag>Best Match</Tag>}
                <h3 className="p-display text-[15px] leading-snug mt-0.5"
                  style={{ color: i === 0 ? W : N }}>{c.title}</h3>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  <span className="text-[12px] p-mono" style={{ color: i === 0 ? `${W}80` : SL }}>
                    {c.hours} hrs
                  </span>
                  <span className="text-[12px]" style={{ color: i === 0 ? O : SL }}>
                    {c.credential}
                  </span>
                </div>
              </div>
            </div>
          </button>
        ))}
        <div className="text-center pt-2">
          <p className="text-[13px]" style={{ color: SL }}>Each pathway ends in a nationally recognized credential.</p>
        </div>
      </main>
      <footer className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0' }}>
        <Btn onClick={() => update({ screen: 'home' })} className="w-full" data-testid="prof-go-home">
          Start Learning <ArrowRight className="w-4 h-4" />
        </Btn>
      </footer>
    </>
  );
};

const HomeScreen = ({ state, update }) => {
  const { learner, courses, activeTab, xp } = state;

  const handleTabChange = (tab) => update({ activeTab: tab });

  const CourseCard = ({ course }) => {
    const cs = courses[course.id] || {};
    const nodesComplete = cs.nodesComplete || [];
    const pct = Math.round((nodesComplete.length / course.nodes.length) * 100);
    const passed = cs.finalPassed;

    return (
      <button onClick={() => update({ activeCourse: course.id, screen: 'course-overview' })}
        className="w-full text-left rounded-[14px] overflow-hidden"
        style={{ border: `1px solid ${passed ? SUC : '#e2e8f0'}` }}
        data-testid={`prof-course-${course.id}`}>
        <div className="px-5 pt-4 pb-3 flex items-start gap-3"
          style={{ background: passed ? `${SUC}08` : SURF }}>
          <div className="w-10 h-10 rounded-[8px] shrink-0 flex items-center justify-center text-xl"
            style={{ background: N }}>{course.badgeEmoji}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              {passed && <Tag color={SUC}>Completed</Tag>}
              {nodesComplete.length > 0 && !passed && <Tag>In Progress</Tag>}
            </div>
            <h3 className="p-display text-[15px] leading-snug" style={{ color: N }}>{course.title}</h3>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-[11px] p-mono" style={{ color: SL }}>{course.hours} hrs</span>
              <span className="text-[11px]" style={{ color: SL }}>·</span>
              <span className="text-[11px]" style={{ color: SL }}>{course.credential}</span>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 shrink-0 mt-1" style={{ color: SL }} />
        </div>
        {nodesComplete.length > 0 && !passed && (
          <div className="px-5 pb-3">
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: O }} />
            </div>
            <p className="text-[11px] mt-1" style={{ color: SL }}>
              {nodesComplete.length} of {course.nodes.length} nodes complete
            </p>
          </div>
        )}
      </button>
    );
  };

  const CredsTab = () => {
    const earned = PROF_COURSES_LIST.filter(c => courses[c.id]?.finalPassed);
    const locked = PROF_COURSES_LIST.filter(c => !courses[c.id]?.finalPassed);
    return (
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        <p className="p-display text-[20px]" style={{ color: N }}>Credential Wallet</p>
        {earned.map(c => {
          const cs = courses[c.id];
          const dateStr = cs.certDate ? new Date(cs.certDate).toLocaleDateString() : '';
          return (
            <div key={c.id} className="rounded-[12px] p-4" style={{ background: N }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-xl"
                  style={{ background: c.badgeAccent }}>{c.badgeEmoji}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold" style={{ color: W }}>{c.credential}</p>
                  <p className="text-[11px]" style={{ color: `${W}70` }}>{c.issuer}</p>
                  <p className="text-[11px] p-mono mt-0.5" style={{ color: O }}>{cs.certId}</p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Tag color={SUC}>Earned {dateStr}</Tag>
                <Tag>{c.hours} hrs</Tag>
              </div>
            </div>
          );
        })}
        {locked.map(c => (
          <div key={c.id} className="rounded-[12px] p-4 flex items-center gap-3" style={{ background: SURF, opacity: 0.6 }}>
            <Lock className="w-5 h-5 shrink-0" style={{ color: SL }} />
            <div>
              <p className="text-[13px] font-semibold" style={{ color: N }}>{c.credential}</p>
              <p className="text-[11px]" style={{ color: SL }}>Complete {c.title} to unlock</p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const JobsTab = () => {
    const earned = PROF_COURSES_LIST.filter(c => courses[c.id]?.finalPassed).map(c => c.id);
    const hasMatches = earned.length > 0;
    return (
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        <p className="p-display text-[20px]" style={{ color: N }}>Employer Matches</p>
        {!hasMatches && (
          <div className="rounded-[12px] p-5 text-center" style={{ background: SURF }}>
            <Briefcase className="w-8 h-8 mx-auto mb-2" style={{ color: SL }} />
            <p className="text-[14px]" style={{ color: SL }}>Complete a course to unlock employer matches tailored to your credentials.</p>
          </div>
        )}
        {earned.map(id => (PROF_EMPLOYERS[id] || []).map((emp, i) => (
          <div key={`${id}-${i}`} className="rounded-[12px] p-4" style={{ border: '1px solid #e2e8f0' }}>
            <p className="text-[14px] font-bold" style={{ color: N }}>{emp.name}</p>
            <p className="text-[13px] mt-0.5" style={{ color: '#1c1c1e' }}>{emp.title}</p>
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className="text-[12px] p-mono" style={{ color: O }}>{emp.salary}</span>
              <span className="text-[12px]" style={{ color: SL }}>{emp.location}</span>
              <span className="text-[12px]" style={{ color: SL }}>{emp.openings} openings</span>
            </div>
          </div>
        )))}
      </div>
    );
  };

  const ReentryTab = () => {
    const completedCount = PROF_COURSES_LIST.filter(c => courses[c.id]?.finalPassed).length;
    return (
      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
        <p className="p-display text-[20px]" style={{ color: N }}>Reentry Plan</p>
        {[
          { label: 'Account created', done: true },
          { label: 'Intake assessment complete', done: true },
          { label: 'Learning pathway selected', done: true },
          { label: 'First credential earned', done: completedCount >= 1 },
          { label: 'Credential pathway identified', done: completedCount >= 1 },
          { label: 'Employer match viewed', done: completedCount >= 1 },
          { label: 'All three credentials earned', done: completedCount === 3 },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-3 rounded-[10px] px-4 py-3"
            style={{ background: item.done ? `${SUC}0d` : SURF }}>
            <div className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ background: item.done ? SUC : '#e2e8f0' }}>
              {item.done ? <Check className="w-3.5 h-3.5 text-white" /> : <span className="w-2 h-2 rounded-full" style={{ background: '#cbd5e1' }} />}
            </div>
            <span className="text-[14px] font-semibold" style={{ color: item.done ? SUC : SL }}>{item.label}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {/* Header */}
      <div className="px-5 pt-4 pb-3 border-b flex items-center justify-between" style={{ borderColor: '#e2e8f0' }}>
        <div>
          <p className="text-[12px] font-bold uppercase tracking-wider" style={{ color: SL }}>Welcome back</p>
          <h1 className="p-display text-[22px]" style={{ color: N, margin: 0 }}>
            {learner.firstName} {learner.lastInitial}.
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-full px-3 py-1.5 flex items-center gap-1.5"
            style={{ background: N }}>
            <Zap className="w-3.5 h-3.5" style={{ color: O }} />
            <span className="text-[13px] font-bold p-mono" style={{ color: W }}>{xp} XP</span>
          </div>
        </div>
      </div>

      {activeTab === 'home' && (
        <main className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {PROF_COURSES_LIST.map(c => <CourseCard key={c.id} course={c} />)}
        </main>
      )}
      {activeTab === 'creds' && <CredsTab />}
      {activeTab === 'jobs' && <JobsTab />}
      {activeTab === 'reentry' && <ReentryTab />}

      <ProfBottomNav active={activeTab} onChange={handleTabChange} courses={courses} />
    </>
  );
};

const CourseOverview = ({ state, update }) => {
  const course = PROF_COURSES[state.activeCourse];
  const cs = state.courses[course.id] || {};
  const nodesComplete = cs.nodesComplete || [];
  const firstNode = course.nodes[nodesComplete.length] || course.nodes[0];
  const inProgress = nodesComplete.length > 0;

  return (
    <>
      <ScreenHeader title={course.title} subtitle="Course Overview"
        onBack={() => update({ screen: 'home', activeCourse: null })} />
      <main className="flex-1 overflow-y-auto">
        {/* Hero */}
        <div className="px-5 pt-4 pb-5 space-y-3"
          style={{ background: `linear-gradient(140deg, ${N} 0%, #081827 100%)` }}>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-[12px] flex items-center justify-center text-3xl"
              style={{ background: `${course.badgeAccent}33` }}>{course.badgeEmoji}</div>
            <div>
              <div className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 mb-1"
                style={{ background: `${O}20` }}>
                <Award className="w-3 h-3" style={{ color: O }} />
                <span className="text-[11px] font-bold" style={{ color: O }}>Credential</span>
              </div>
              <p className="text-[14px] font-semibold leading-snug" style={{ color: W }}>{course.credential}</p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <div className="rounded-[8px] px-3 py-2" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: `${W}70` }}>Hours</p>
              <p className="text-[18px] font-bold p-mono" style={{ color: W }}>{course.hours}</p>
            </div>
            <div className="rounded-[8px] px-3 py-2" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: `${W}70` }}>Nodes</p>
              <p className="text-[18px] font-bold p-mono" style={{ color: W }}>{course.nodes.length}</p>
            </div>
            <div className="rounded-[8px] px-3 py-2" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: `${W}70` }}>XP</p>
              <p className="text-[18px] font-bold p-mono" style={{ color: W }}>{course.xpAvailable}</p>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          {course.nabcepNote && (
            <div className="rounded-[10px] px-4 py-3 flex items-start gap-2"
              style={{ background: `${O}0f`, border: `1px solid ${O}33` }}>
              <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: O }} />
              <p className="text-[13px] leading-relaxed" style={{ color: N }}>{course.nabcepNote}</p>
            </div>
          )}
          <p className="text-[15px] leading-relaxed" style={{ color: '#1c1c1e' }}>{course.description}</p>
          <div>
            <p className="text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: SL }}>Curriculum</p>
            <ol className="space-y-2 list-none p-0">
              {course.nodes.map((n, i) => {
                const done = nodesComplete.includes(n.id);
                return (
                  <li key={n.id} className="flex items-center gap-3 rounded-[10px] px-4 py-3"
                    style={{ background: done ? `${SUC}0d` : SURF }}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center text-[12px] font-bold"
                      style={{ background: done ? SUC : n.kind === 'final' ? N : '#e2e8f0', color: done || n.kind === 'final' ? W : SL }}>
                      {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-[14px] font-semibold" style={{ color: done ? SUC : N }}>{n.title}</p>
                      <p className="text-[11px]" style={{ color: SL }}>{n.subtitle}</p>
                    </div>
                    {n.kind === 'final' && !done && <Lock className="w-3.5 h-3.5" style={{ color: SL }} />}
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </main>
      <footer className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0' }}>
        <Btn onClick={() => update({ screen: 'mood', moodDone: false })} className="w-full"
          data-testid="prof-start-module">
          {inProgress ? 'Continue Course' : 'Start Course'} <ArrowRight className="w-4 h-4" />
        </Btn>
      </footer>
    </>
  );
};

const MoodCheckin = ({ state, update }) => {
  const MOODS = [
    { label: 'Rough day', emoji: '😔' },
    { label: 'Unsure', emoji: '😐' },
    { label: 'Okay', emoji: '🙂' },
    { label: 'Good', emoji: '😊' },
    { label: 'Ready', emoji: '💪' },
  ];
  const RESPONSES = {
    'Rough day': "That's okay. Learning is how we move forward. Let's start with just one node today.",
    'Unsure': "Totally normal. Just read through the material at your own pace.",
    'Okay': "Okay is enough. Let's build on it.",
    'Good': "Good energy. Let's put it to work.",
    'Ready': "Let's go. This is your time.",
  };
  const [chosen, setChosen] = useState(null);

  const proceed = () => {
    const course = PROF_COURSES[state.activeCourse];
    const cs = state.courses[state.activeCourse] || {};
    const nodesComplete = cs.nodesComplete || [];
    const nodeIdx = Math.min(nodesComplete.length, course.nodes.length - 1);
    update({ screen: 'node', activeNode: nodeIdx, nodePhase: 'lesson' });
  };

  return (
    <>
      <ScreenHeader title="How are you feeling today?" subtitle="Mood Check-In" />
      <main className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
        <div className="grid grid-cols-5 gap-2">
          {MOODS.map(m => (
            <button key={m.label} onClick={() => setChosen(m.label)}
              className="flex flex-col items-center gap-1 rounded-[10px] p-2 transition-all"
              style={{ background: chosen === m.label ? N : SURF, border: `1px solid ${chosen === m.label ? N : '#e2e8f0'}` }}
              data-testid={`prof-mood-${m.label.replace(/\s+/g, '-').toLowerCase()}`}>
              <span className="text-2xl">{m.emoji}</span>
              <span className="text-[10px] font-semibold text-center leading-tight"
                style={{ color: chosen === m.label ? W : SL }}>{m.label}</span>
            </button>
          ))}
        </div>
        {chosen && (
          <div className="rounded-[12px] px-4 py-3.5" style={{ background: `${O}0f`, border: `1px solid ${O}33` }}>
            <p className="text-[14px] leading-relaxed" style={{ color: N }}>{RESPONSES[chosen]}</p>
          </div>
        )}
      </main>
      <footer className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0' }}>
        <Btn onClick={proceed} disabled={!chosen}
          className={`w-full ${!chosen ? 'opacity-40 pointer-events-none' : ''}`}
          data-testid="prof-mood-begin">
          Begin Lesson <ArrowRight className="w-4 h-4" />
        </Btn>
      </footer>
    </>
  );
};

/* ── Node screen (lessons 1-4) ───────────────────────────── */
const NodeScreen = ({ state, update, onXp }) => {
  const course = PROF_COURSES[state.activeCourse];
  const node = course.nodes[state.activeNode];
  const cs = state.courses[course.id] || {};
  const nodesComplete = cs.nodesComplete || [];
  const phase = state.nodePhase;
  const isFinal = node.kind === 'final';
  const [scenarioDone, setScenarioDone] = useState(false);

  // for checking if lesson phase can advance
  const [cardsFull, setCardsFull] = useState(false);
  useEffect(() => { setCardsFull(false); setScenarioDone(false); }, [state.activeNode, phase]);

  // SAFETY node: track scenario completion for +XP
  const handleSafetyScenario = () => {
    if (!scenarioDone) {
      onXp(node.scenario?.xp || 10);
      setScenarioDone(true);
    }
  };

  const goToQuiz = () => update({ nodePhase: 'quiz' });

  const handleCorrect = () => {
    onXp(10);
  };

  const handleWrong = () => {
    update(s => ({ ...s, hearts: Math.max(0, (s.hearts || 0) - 1) }));
  };

  const handleNodeComplete = () => {
    const nodeId = node.id;
    const alreadyDone = nodesComplete.includes(nodeId);
    if (!alreadyDone) {
      onXp(node.xp || 25);
    }
    const newNodesComplete = alreadyDone ? nodesComplete : [...nodesComplete, nodeId];

    // Check if there's a next node
    const nextIdx = state.activeNode + 1;
    if (nextIdx < course.nodes.length) {
      const nextNode = course.nodes[nextIdx];
      update(s => ({
        ...s,
        activeNode: nextIdx,
        nodePhase: 'lesson',
        courses: {
          ...s.courses,
          [course.id]: {
            ...cs,
            nodesComplete: newNodesComplete,
          },
        },
      }));
    } else {
      // All done — shouldn't happen (final handles its own completion)
      update(s => ({
        ...s,
        screen: 'home',
        courses: {
          ...s.courses,
          [course.id]: { ...cs, nodesComplete: newNodesComplete },
        },
      }));
    }
  };

  const handleFinalPass = (score, total) => {
    const certId = `ASST-${new Date().getFullYear()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
    onXp(100);
    update(s => ({
      ...s,
      screen: 'badge-unlock',
      courses: {
        ...s.courses,
        [course.id]: {
          ...cs,
          nodesComplete: [...nodesComplete, node.id],
          finalPassed: true,
          certId,
          certDate: Date.now(),
          failedAt: null,
        },
      },
    }));
  };

  const handleFinalFail = (score, total) => {
    update(s => ({
      ...s,
      screen: 'fail',
      courses: {
        ...s.courses,
        [course.id]: {
          ...cs,
          failedAt: Date.now(),
        },
      },
    }));
  };

  const pct = Math.round((state.activeNode / course.nodes.length) * 100);

  return (
    <>
      <PathwayRail course={course} nodesComplete={nodesComplete} activeNode={state.activeNode} />

      {/* Sub-header */}
      <div className="px-5 py-2 border-b flex items-center justify-between" style={{ borderColor: '#e2e8f0' }}>
        <button onClick={() => update({ screen: 'course-overview' })} className="flex items-center gap-1 text-[13px] font-semibold" style={{ color: SL }}>
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          <span className="text-[12px] font-semibold p-mono" style={{ color: SL }}>
            {state.activeNode + 1}/{course.nodes.length}
          </span>
          {phase === 'quiz' && !isFinal && <Hearts count={state.hearts} />}
        </div>
      </div>

      {/* Node heading */}
      <div className="px-5 pt-3 pb-2">
        <Tag>{node.subtitle || `NODE ${state.activeNode + 1}`}</Tag>
        <h2 className="p-display text-[20px] mt-1" style={{ color: N, margin: '4px 0 0' }}>{node.title}</h2>
      </div>

      {isFinal ? (
        <div className="flex-1 overflow-y-auto flex flex-col">
          {phase === 'lesson' ? (
            <>
              <div className="px-5 py-3 space-y-3 flex-1">
                <p className="text-[15px] leading-relaxed" style={{ color: '#1c1c1e' }}>{node.intro}</p>
                <div className="rounded-[10px] p-4" style={{ background: SURF }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-4 h-4" style={{ color: N }} />
                    <p className="text-[13px] font-bold" style={{ color: N }}>Pass {node.passCount}/{node.quiz.length} questions ({Math.round(node.passScore * 100)}%+)</p>
                  </div>
                  <p className="text-[13px]" style={{ color: SL }}>Hearts are suspended during the final quiz. Your score is tracked only.</p>
                </div>
              </div>
              <div className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0' }}>
                <Btn onClick={goToQuiz} className="w-full" data-testid="prof-take-final">
                  Take Final Quiz <ArrowRight className="w-4 h-4" />
                </Btn>
              </div>
            </>
          ) : (
            <FinalQuiz quiz={node.quiz} passCount={node.passCount}
              onPass={handleFinalPass} onFail={handleFinalFail} />
          )}
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto">
            {phase === 'lesson' && (
              <>
                {node.kind === 'story' && <StoryNode node={node} />}
                {node.kind === 'cards' && <CardsNode node={node} />}
                {node.kind === 'steps' && <StepsNode node={node} />}
                {node.kind === 'loop' && <LoopNode node={node} />}
                {node.kind === 'tiers' && <TiersNode node={node} />}
                {node.kind === 'register' && <RegisterNode node={node} />}
                {node.kind === 'checklist' && <ChecklistNode node={node} />}
                {node.kind === 'safety' && (
                  <SafetyNode node={node} onScenarioDone={handleSafetyScenario} scenarioDone={scenarioDone} />
                )}
              </>
            )}
            {phase === 'quiz' && (
              <QuizRunner quiz={node.quiz} hearts={state.hearts}
                onCorrect={handleCorrect} onWrong={handleWrong}
                onComplete={handleNodeComplete} courseId={course.id} />
            )}
          </div>
          {phase === 'lesson' && (
            <footer className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0' }}>
              <Btn onClick={goToQuiz} className="w-full" data-testid="prof-to-quiz">
                Take Knowledge Check <ArrowRight className="w-4 h-4" />
              </Btn>
            </footer>
          )}
        </>
      )}
    </>
  );
};

/* ── Pass / Fail screens ─────────────────────────────────── */
const PassScreen = ({ state, update }) => {
  const course = PROF_COURSES[state.activeCourse];
  const cs = state.courses[course.id] || {};

  return (
    <>
      <ScreenHeader title="You Passed!" subtitle="Assessment Complete" />
      <main className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
        <div className="text-center py-6 space-y-3">
          <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center text-4xl"
            style={{ background: `${SUC}15`, border: `3px solid ${SUC}` }}>
            {course.badgeEmoji}
          </div>
          <div>
            <h2 className="p-display text-[24px]" style={{ color: N }}>{course.badgeName}</h2>
            <p className="text-[14px] mt-1" style={{ color: SL }}>Badge unlocked · {course.credential}</p>
          </div>
        </div>

        <CertificateCard course={course} learner={state.learner}
          certId={cs.certId} certDate={cs.certDate}
          onBack={() => update({ screen: 'home', activeTab: 'creds' })} />
      </main>

      <footer className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0' }}>
        <Btn onClick={() => update({ screen: 'home', activeCourse: null, activeTab: 'creds' })} className="w-full"
          data-testid="prof-view-wallet">
          View Credential Wallet <ArrowRight className="w-4 h-4" />
        </Btn>
        <button onClick={() => update({ screen: 'home', activeCourse: null, activeTab: 'home' })}
          className="w-full mt-2 py-3 text-[14px] font-semibold rounded-[12px]"
          style={{ background: SURF, color: N }}>
          Back to All Courses
        </button>
      </footer>
    </>
  );
};

const FailScreen = ({ state, update }) => {
  const course = PROF_COURSES[state.activeCourse];
  const cs = state.courses[course.id] || {};
  const [unlocked, setUnlocked] = useState(false);

  const retry = () => {
    update({ screen: 'node', activeNode: course.nodes.length - 1, nodePhase: 'quiz' });
  };

  return (
    <>
      <ScreenHeader title="Keep Going" subtitle="Assessment Result" />
      <main className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
        <div className="rounded-[14px] p-5 text-center" style={{ background: SURF }}>
          <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#fef3c7' }}>
            <RefreshCw className="w-7 h-7" style={{ color: '#d97706' }} />
          </div>
          <h2 className="p-display text-[22px] mb-1" style={{ color: N }}>You're building real skills.</h2>
          <p className="text-[14px]" style={{ color: SL }}>Review the lessons and try again when the timer unlocks.</p>
        </div>

        {cs.failedAt && !unlocked && (
          <RetakeTimer failedAt={cs.failedAt} onUnlocked={() => setUnlocked(true)} />
        )}

        <div>
          <p className="text-[12px] font-bold uppercase tracking-wider mb-2" style={{ color: SL }}>Review these nodes:</p>
          {course.nodes.filter(n => n.kind !== 'final').map((n, i) => (
            <button key={n.id}
              onClick={() => update({ screen: 'node', activeNode: i, nodePhase: 'lesson' })}
              className="w-full flex items-center gap-3 text-left rounded-[10px] px-4 py-3 mb-2"
              style={{ background: SURF, border: '1px solid #e2e8f0' }}>
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold"
                style={{ background: N, color: W }}>{i + 1}</span>
              <span className="text-[14px] font-semibold" style={{ color: N }}>{n.title}</span>
              <ChevronRight className="w-4 h-4 ml-auto" style={{ color: SL }} />
            </button>
          ))}
        </div>
      </main>
      <footer className="px-5 py-4 border-t" style={{ borderColor: '#e2e8f0' }}>
        {unlocked ? (
          <Btn onClick={retry} className="w-full" data-testid="prof-retry">
            Retake Final Quiz <ArrowRight className="w-4 h-4" />
          </Btn>
        ) : (
          <button onClick={() => update({ screen: 'home' })}
            className="w-full rounded-[12px] py-3 text-[15px] font-semibold"
            style={{ background: SURF, color: N }}>
            Return to Home
          </button>
        )}
      </footer>
    </>
  );
};

/* ── ProfessionalLearner root ────────────────────────────── */
const ProfessionalLearner = () => {
  const [state, update] = useProfState();
  const [xpFloat, setXpFloat] = useState(null);

  const awardXp = useCallback((amount) => {
    update(s => ({ ...s, xp: (s.xp || 0) + amount }));
    setXpFloat(amount);
  }, [update]);

  const clearFloat = useCallback(() => setXpFloat(null), []);

  const { screen } = state;

  return (
    <div className="flex flex-col h-full overflow-hidden relative" style={{ background: W, fontFamily: 'var(--p-font-body)' }}>
      <style>{`
        @keyframes xpFloat { from { transform: translateY(0); opacity: 1; } to { transform: translateY(-32px); opacity: 0; } }
        .prof-shake { animation: profShake 0.5s ease; }
        @keyframes profShake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-4px)} 40%{transform:translateX(4px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }
      `}</style>

      {xpFloat && <XpFloat amount={xpFloat} onDone={clearFloat} />}

      {screen === 'signup-a' && <SignupA state={state} update={update} />}
      {screen === 'signup-b' && <SignupB state={state} update={update} />}
      {screen === 'signup-c' && <SignupC state={state} update={update} />}
      {screen === 'home' && <HomeScreen state={state} update={update} />}
      {screen === 'course-overview' && <CourseOverview state={state} update={update} />}
      {screen === 'mood' && <MoodCheckin state={state} update={update} />}
      {(screen === 'node' || screen === 'final') && (
        <NodeScreen state={state} update={update} onXp={awardXp} />
      )}
      {screen === 'badge-unlock' && (
        <BadgeUnlock course={PROF_COURSES[state.activeCourse]}
          onDone={() => update({ screen: 'pass' })} />
      )}
      {screen === 'pass' && <PassScreen state={state} update={update} />}
      {screen === 'fail' && <FailScreen state={state} update={update} />}
    </div>
  );
};

export default ProfessionalLearner;
